import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import {
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import theme from 'theme';
import { LoadingPage, Empty, Icons, PriceLabel } from 'components';
import { useReactToPrint } from 'react-to-print';
import InvoiceDetailsServices from './services/InvoiceDetailsServices';
import { useCurrencyUnit } from 'hooks';

const ComponentToPrint = React.forwardRef(({ data }, ref) => {
  const classes = useStyles();
  const currencyText = useCurrencyUnit(false);

  return (
    <div
      style={{
        direction: 'rtl',
        width: '95%',
        margin: 'auto',
      }}
      className={classes.root}
      ref={ref}
    >
      <Grid container className={classes.detailsPage}>
        <Grid item xs={4} className={classes.item}>
          <Typography className={classes.commonPrint} color="textSecondary" gutterBottom>
            شماره فاکتور
          </Typography>
          <Typography className={classes.commonPrint} color="textSecondary" gutterBottom>
            {data ? data?.id : '-'}
          </Typography>
        </Grid>

        <Grid item xs={4} className={classes.item}>
          <Typography className={classes.commonPrint} color="textSecondary" gutterBottom>
            شماره قبض
          </Typography>
          <Typography className={classes.commonPrint} color="textSecondary" gutterBottom>
            {data?.billNumber ?? '-'}
          </Typography>
        </Grid>

        <Grid item xs={4} className={classes.item}>
          <Typography className={classes.commonPrint} color="textSecondary" gutterBottom>
            شماره پرداخت
          </Typography>
          <Typography className={classes.commonPrint} color="textSecondary" gutterBottom>
            {data?.paymentBillNumber || '-'}
          </Typography>
        </Grid>

        <Grid item xs={4} className={classes.item}>
          <Typography className={classes.commonPrint} color="textSecondary" gutterBottom>
            نام فروشنده
          </Typography>
          <Typography className={classes.commonPrint} color="textSecondary" gutterBottom>
            {data?.issuerSrv?.name || '-'}
          </Typography>
        </Grid>

        <Grid item xs={4} className={classes.item}>
          <Typography className={classes.commonPrint} color="textSecondary" gutterBottom>
            نام خریدار
          </Typography>
          <Typography className={classes.commonPrint} color="textSecondary" gutterBottom>
            {data?.userSrv?.name || '-'}
          </Typography>
        </Grid>
      </Grid>

      {data?.description && (
        <Grid container className={classes.detailsPage}>
          <Grid item xs={12}>
            <Typography className={classes.commonPrint} color="textSecondary" gutterBottom>
              توضیحات
            </Typography>
            <Typography className={classes.commonPrint} color="textSecondary" gutterBottom>
              {data?.description || '-'}
            </Typography>
          </Grid>
        </Grid>
      )}

      <Grid item xs={12} style={{ marginTop: '2em' }}>
        <Typography component="h3" style={{ marginBottom: '.5em', fontSize: '12px' }}>
          بندهای فاکتور
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow style={{ backgroundColor: 'rgb(238, 241, 245)' }}>
                <TableCell className={classes.commonPrint}>شماره</TableCell>
                <TableCell className={classes.commonPrint}>شرح</TableCell>
                <TableCell className={classes.commonPrint}>مبلغ ({currencyText})</TableCell>
                <TableCell className={classes.commonPrint}>تعداد</TableCell>
                <TableCell className={classes.commonPrint}>جمع ({currencyText})</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.invoiceItemSrvs ? (
                data?.invoiceItemSrvs?.map((row, index) => {
                  const total = row?.amount * row?.quantity;
                  return (
                    <TableRow key={index}>
                      <TableCell component="th" className={classes.commonPrint} scope="row">
                        <div className={classes.breakWord}>{row?.id || '-'}</div>
                      </TableCell>
                      <TableCell className={classes.commonPrint}>{row?.description || '-'}</TableCell>
                      <TableCell className={classes.commonPrint}>
                        {row?.amount ? (
                          <PriceLabel amount={row?.amount} />
                        ) : ('-')
                        }
                      </TableCell>
                      <TableCell className={classes.commonPrint}>{row?.quantity || '-'}</TableCell>
                      <TableCell className={classes.commonPrint}>
                        {total ? (<PriceLabel amount={total} />) : ('-')}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={3}>
                    داده موجود نیست.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <Grid container>
        <Grid className={classes.totalCount} container>
          <Grid item className={classes.dis} lg={3} md={4}>
            <Typography style={{ marginLeft: theme.spacing(1), fontSize: '12px' }}>
              مالیات :
            </Typography>
            <Typography style={{ fontSize: '12px' }}>
              <PriceLabel  amount={data?.vat} />
            </Typography>
          </Grid>
        </Grid>

        <Grid container className={classes.totalCount}>
          <Grid item className={classes.dis} lg={3} md={4}>
            <Typography style={{ marginLeft: theme.spacing(1), fontSize: '12px' }}>
              مجموع بدون تخفیف:
            </Typography>
            <Typography style={{ fontSize: '12px' }}>
              <PriceLabel  amount={data?.totalAmountWithoutDiscount} />
            </Typography>
          </Grid>
        </Grid>

        <Grid container className={classes.totalCount}>
          <Grid item className={classes.dis} lg={3} md={4}>
            <Typography style={{ marginLeft: theme.spacing(1), fontSize: '12px' }}>
              جمع کل:
            </Typography>
            <Typography style={{ fontSize: '12px' }}>
              <PriceLabel  amount={data?.payableAmount} />
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
});

const InvoiceDetails = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [more, setMore] = useState(false);
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const componentRef = useRef();
  const currencyText = useCurrencyUnit(false);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const res = await InvoiceDetailsServices.getInvoice(id);
        setData(res?.result[0] || {});
      } catch (error) {
        setIsError(true)
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const showMore = () => {
    setMore(!more);
  };

  const sentence = data?.description || '-';
  const loadingStyle = { marginTop: '32px' }
  return (
    <>
      <div style={{ display: 'none' }}>
        <ComponentToPrint data={data} ref={componentRef} />
      </div>
      {
        isError && <Card style={{ minHeight: '500px', 'justifyContent': 'center', 'alignItems': 'center', display: 'flex', marginTop: '32px' }}>
          <CardContent>
            <Empty />
          </CardContent>
        </Card >
      }

      {isLoading ? (
        <LoadingPage customStyle={loadingStyle} />
      ) : (!isError &&
        <div className={classes.root}>
          <Card>
            <CardContent>
              <Grid container alignItems="center" justifyContent="flex-end">
                <Button
                  color="primary"
                  startIcon={<Icons icon="print" color="#fff" size="18" />}
                  variant="contained"
                  onClick={handlePrint}
                  style={{ marginLeft: '1em' }}
                >
                  چاپ
                </Button>
                <Button
                  color="primary"
                  endIcon={<Icons icon="CaretLeft" color="#2b3990" size="25" />}
                  variant="outlined"
                  onClick={() => navigate('/InvoiceCreditor')}
                >
                  بازگشت به لیست فاکتورها
                </Button>
              </Grid>

              <Grid container className={classes.detailsPage}>
                <Grid item md={3} sm={6} xs={12} className={classes.item}>
                  <Typography className={classes.title} color="textSecondary" gutterBottom>
                    شماره فاکتور
                  </Typography>
                  <Typography className={classes.response} color="textSecondary" gutterBottom>
                    {data ? data.id : '-'}
                  </Typography>
                </Grid>

                <Grid item md={3} sm={6} xs={12} className={classes.item}>
                  <Typography className={classes.title} color="textSecondary" gutterBottom>
                    شماره قبض
                  </Typography>
                  <Typography className={classes.response} color="textSecondary" gutterBottom>
                    {data?.billNumber || '-'}
                  </Typography>
                </Grid>

                <Grid item md={3} sm={6} xs={12} className={classes.item}>
                  <Typography className={classes.title} color="textSecondary" gutterBottom>
                    شماره پرداخت
                  </Typography>
                  <Typography className={classes.response} color="textSecondary" gutterBottom>
                    {data?.paymentBillNumber || '-'}
                  </Typography>
                </Grid>
                <Grid item md={3} sm={6} xs={12} className={classes.item}>
                  <Typography className={classes.title} color="textSecondary" gutterBottom>
                    RRN
                  </Typography>
                  <Typography className={classes.response} color="textSecondary" gutterBottom>
                    {data?.rrn || '-'}
                  </Typography>
                </Grid>

                <Grid item md={3} sm={6} xs={12} className={classes.item}>
                  <Typography className={classes.title} color="textSecondary" gutterBottom>
                    نام فروشنده
                  </Typography>
                  <Typography className={classes.response} color="textSecondary" gutterBottom>
                    {data?.business?.name || '-'}
                  </Typography>
                </Grid>
                <Grid item md={3} sm={6} xs={12} className={classes.item}>
                  <Typography className={classes.title} color="textSecondary" gutterBottom>
                    نام خریدار
                  </Typography>
                  <Typography className={classes.response} color="textSecondary" gutterBottom>
                    {data?.userSrv?.name || '-'}
                  </Typography>
                </Grid>
              </Grid>

              {data?.description ? (
                <Grid container className={classes.detailsPage}>
                  <Grid item xs={12}>
                    <Typography className={classes.title} color="textSecondary" gutterBottom>
                      توضیحات
                    </Typography>
                    <Typography className={classes.response} color="textSecondary" gutterBottom>
                      {sentence.length < 255 || more ? sentence : `${sentence.slice(0, 257)}...`}
                      {sentence.length > 255 && more == false ? (
                        <span className={classes.hiddenAriea} />
                      ) : null}
                    </Typography>
                    {sentence.length > 255 && (
                      <Button color="primary" onClick={showMore}>
                        {more ? 'بستن' : 'نمایش بیشتر'}
                      </Button>
                    )}
                  </Grid>
                </Grid>
              ) : (
                ''
              )}

              <Grid item xs={12} style={{ marginTop: '2em' }}>
                <Typography component="h3" style={{ marginBottom: '.5em', fontSize: '1em' }}>
                  بندهای فاکتور
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.headerTable} align="center">
                          شماره
                        </TableCell>
                        <TableCell className={classes.headerTable} align="center">
                          شرح
                        </TableCell>
                        <TableCell className={classes.headerTable} align="center">
                          مبلغ ({currencyText})
                        </TableCell>
                        <TableCell className={classes.headerTable} align="center">
                          تعداد
                        </TableCell>
                        <TableCell className={classes.headerTable} align="center">
                          جمع ({currencyText})
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data?.invoiceItemSrvs ? (
                        data?.invoiceItemSrvs?.map((row, index) => {
                          const total = row.amount * row.quantity;
                          return (
                            <TableRow key={index}>
                              <TableCell
                                component="th"
                                align="center"
                                className={classes.tableCell}
                                scope="row"
                              >
                                <div className={classes.breakWord}>{row?.id || '-'}</div>
                              </TableCell>
                              <TableCell className={classes.tableCell} align="center">
                                {row?.description || '-'}
                              </TableCell>
                              <TableCell align="center" className={classes.tableCell}>
                                {row?.amount ? (<PriceLabel  amount={row?.amount} />) : ('-')}
                              </TableCell>
                              <TableCell align="center" className={classes.tableCell}>
                                {row?.quantity || '-'}
                              </TableCell>
                              <TableCell align="center" className={classes.tableCell}>
                                {
                                  total ? ( <PriceLabel  amount={total} />) : ('-')
                                }

                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell align="center" colSpan={3}>
                            داده موجود نیست.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              <Grid container>
                <Grid className={classes.totalCount} container>
                  <Grid item className={classes.dis} lg={3} md={4}>
                    <Typography className={classes.title} style={{ marginLeft: theme.spacing(1) }}>
                      مالیات :
                    </Typography>
                    <Typography className={classes.sum}>
                      <PriceLabel  amount={data?.vat} />
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container className={classes.totalCount}>
                  <Grid item className={classes.dis} lg={3} md={4}>
                    <Typography className={classes.title} style={{ marginLeft: theme.spacing(1) }}>
                      مجموع بدون تخفیف:
                    </Typography>
                    <Typography className={classes.sum}>
                      <PriceLabel  amount={data?.totalAmountWithoutDiscount} />
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container className={classes.totalCount}>
                  <Grid item className={classes.dis} lg={3} md={4}>
                    <Typography className={classes.title} style={{ marginLeft: theme.spacing(1) }}>
                      جمع کل:
                    </Typography>
                    <Typography className={classes.sum}>
                      <PriceLabel  amount={data?.payableAmount} />
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default InvoiceDetails;

const useStyles = makeStyles((theme) => ({
  commonPrint: {
    fontSize: '12px',
    color: 'black',
  },
  root: {
    padding: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(4, 0),
    },
  },
  title: {
    color: '#22517f',
    fontWeight: 'bold',
    fontSize: '12px',
  },
  response: {
    color: '#005277',
    position: 'relative',
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 'normal',
    letterSpacing: 'normal',
  },
  detailsPage: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1),
  },
  hiddenAriea: {
    backgroundColor: '#ffffffa6',
    width: '100%',
    height: '20px',
    position: 'absolute',
    bottom: 0,
    right: 0,
  },
  sum: {
    fontSize: '16px',
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 'normal',
    letterSpacing: 'normal',
    color: '#263238',
  },
  totalCount: {
    display: 'flex',
    alignItems: 'flex-end',
    marginTop: theme.spacing(2),
    fontSize: '11px',
  },
  headerTable: {
    backgroundColor: 'rgb(238, 241, 245)',
    textAlign: 'left',
    color: 'rgb(29, 64, 101)',
    fontSize: '11px',
  },
  tableCell: {
    textAlign: 'left',
  },
  backDrop: {
    marginTop: theme.spacing(5),
  },
  dis: {
    display: 'flex !important',
  },
  item: { marginBottom: '1em' },
}));
